import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Sliders, ArrowUp, ArrowDown, ArrowUpDown, RotateCcw } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";

const KPI_TYPES = [
  { v: "presentasi", label: "Presentasi" },
  { v: "principal_relationship", label: "Principal Relationship" },
  { v: "technical_proposal", label: "Technical Proposal" },
  { v: "sertifikasi", label: "Sertifikasi" },
  { v: "self_skill", label: "Self Skill Development" },
];

export default function Performance() {
  const { user, isReadOnly } = useAuth();
  const isLeader = user?.role === "team_leader";
  const canViewTeam = ["team_leader", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [weights, setWeights] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ kpi_type: "presentasi", date: new Date().toISOString().slice(0, 10), amount: 1, note: "" });
  const [openWeights, setOpenWeights] = useState(false);
  const [memberFilter, setMemberFilter] = useState("all");
  const [kpiFilter, setKpiFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState({ key: "date", direction: "desc" });

  const userNames = useMemo(() => {
    const names = { [user?.id]: user?.name || user?.email || "Saya" };
    users.forEach((item) => { names[item.id] = item.name || item.email; });
    return names;
  }, [users, user]);

  const filteredItems = useMemo(() => {
    const result = items.filter((item) => (
      (memberFilter === "all" || item.user_id === memberFilter) &&
      (kpiFilter === "all" || item.kpi_type === kpiFilter) &&
      (!dateFrom || item.date >= dateFrom) &&
      (!dateTo || item.date <= dateTo)
    ));
    return result.sort((a, b) => {
      const left = sort.key === "user" ? (userNames[a.user_id] || "") : a[sort.key];
      const right = sort.key === "user" ? (userNames[b.user_id] || "") : b[sort.key];
      const comparison = sort.key === "amount" ? Number(left || 0) - Number(right || 0) : String(left || "").localeCompare(String(right || ""));
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [items, memberFilter, kpiFilter, dateFrom, dateTo, sort, userNames]);

  const summary = useMemo(() => {
    const grouped = {};
    filteredItems.forEach((item) => {
      const key = item.user_id || "unknown";
      if (!grouped[key]) grouped[key] = { userId: key, entries: 0, amount: 0, types: new Set() };
      grouped[key].entries += 1;
      grouped[key].amount += Number(item.amount || 0);
      grouped[key].types.add(item.kpi_type);
    });
    return Object.values(grouped).sort((a, b) => b.amount - a.amount);
  }, [filteredItems]);

  const [pageItems, ctrl] = usePagination(filteredItems, 10);

  const load = async () => {
    const calls = [api.get("/performance"), api.get("/kpi/weights")];
    if (canViewTeam) calls.push(api.get("/users"));
    const [a, b, u] = await Promise.all(calls);
    setItems(a.data);
    setWeights(b.data);
    if (canViewTeam) setUsers(u.data.filter((item) => item.role === "member"));
  };

  useEffect(() => { load(); }, [canViewTeam]); // eslint-disable-line

  const setSortKey = (key) => setSort((current) => ({
    key,
    direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
  }));
  const SortButton = ({ label, sortKey }) => {
    const active = sort.key === sortKey;
    const Icon = active ? (sort.direction === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return <Button type="button" variant="ghost" className="h-auto p-0 font-inherit" onClick={() => setSortKey(sortKey)}>
      {label}<Icon className="ml-1 h-3 w-3" />
    </Button>;
  };

  const resetFilters = () => {
    setMemberFilter("all"); setKpiFilter("all"); setDateFrom(""); setDateTo("");
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/performance", { ...form, amount: Number(form.amount) });
      toast.success("Entry ditambahkan");
      setForm({ ...form, amount: 1, note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/performance/${id}`);
      toast.success("Entry KPI dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const saveWeights = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      KPI_TYPES.forEach(({ v }) => { payload[v] = Number(weights[v] || 0); });
      await api.put("/kpi/weights", payload);
      toast.success("Bobot KPI tersimpan");
      setOpenWeights(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="performance-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Performance</div>
          <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Input & Track KPI</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Catat aktivitas KPI harian Anda pada 5 dimensi presales.
          </p>
        </div>
        {isLeader && (
          <Dialog open={openWeights} onOpenChange={setOpenWeights}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="open-weights-button">
                <Sliders className="h-4 w-4 mr-2" /> Bobot KPI
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Bobot KPI (Total tidak wajib 100)</DialogTitle></DialogHeader>
              <form onSubmit={saveWeights} className="space-y-3">
                {KPI_TYPES.map(({ v, label }) => (
                  <div key={v} className="grid grid-cols-3 items-center gap-3">
                    <Label className="col-span-2">{label}</Label>
                    <Input
                      type="number" min={0} max={100}
                      value={weights?.[v] ?? 0}
                      onChange={(e) => setWeights({ ...weights, [v]: e.target.value })}
                      data-testid={`weight-${v}`}
                    />
                  </div>
                ))}
                <Button type="submit" className="w-full rounded-sm" data-testid="save-weights-button">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Form */}
      {!isReadOnly && (
      <form onSubmit={submit} className="card-tech p-6 grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="performance-form">
        <div className="md:col-span-2">
          <Label className="label-mini">Jenis KPI</Label>
          <Select value={form.kpi_type} onValueChange={(v) => setForm({ ...form, kpi_type: v })}>
            <SelectTrigger data-testid="kpi-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {KPI_TYPES.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-mini">Tanggal</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="kpi-date-input" />
        </div>
        <div>
          <Label className="label-mini">Jumlah</Label>
          <Input type="number" min={0} step="0.5" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="kpi-amount-input" />
        </div>
        <div className="flex items-end">
          <Button type="submit" className="w-full rounded-sm" data-testid="add-kpi-button">
            <Plus className="h-4 w-4 mr-2" /> Tambah
          </Button>
        </div>
        <div className="md:col-span-5">
          <Label className="label-mini">Catatan</Label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="opsional" data-testid="kpi-note-input" />
        </div>
      </form>
      )}

      {/* List */}
      <div className="card-tech p-0 overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="label-mini">Riwayat</div>
          <h3 className="font-heading text-xl font-bold mt-1">Entry KPI</h3>
        </div>
        <div className="p-4 border-b border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="performance-filters">
          {canViewTeam && <Select value={memberFilter} onValueChange={setMemberFilter}>
            <SelectTrigger data-testid="performance-member-filter"><SelectValue placeholder="Semua member" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua member</SelectItem>{users.map((item) => <SelectItem key={item.id} value={item.id}>{item.name || item.email}</SelectItem>)}</SelectContent>
          </Select>}
          <Select value={kpiFilter} onValueChange={setKpiFilter}>
            <SelectTrigger data-testid="performance-kpi-filter"><SelectValue placeholder="Semua jenis KPI" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua jenis KPI</SelectItem>{KPI_TYPES.map((item) => <SelectItem key={item.v} value={item.v}>{item.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="Tanggal mulai" data-testid="performance-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="Tanggal akhir" data-testid="performance-date-to" />
          <Button type="button" variant="outline" className="rounded-sm" onClick={resetFilters}>
            <RotateCcw className="h-4 w-4 mr-2" /> Reset filter
          </Button>
        </div>
        <div className="border-b border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="performance-summary">
            <thead className="bg-accent text-xs uppercase tracking-widest"><tr>
              <th className="text-left px-6 py-3">Member</th><th className="text-right px-6 py-3">Entry</th><th className="text-right px-6 py-3">Total jumlah</th><th className="text-left px-6 py-3">Dimensi KPI</th>
            </tr></thead>
            <tbody>{summary.length === 0 ? <tr><td colSpan={4} className="px-6 py-5 text-center text-muted-foreground">Belum ada ringkasan.</td></tr> : summary.map((item) => <tr key={item.userId} className="border-t border-border">
              <td className="px-6 py-3 font-medium">{userNames[item.userId] || item.userId}</td><td className="px-6 py-3 text-right mono">{item.entries}</td><td className="px-6 py-3 text-right mono">{item.amount}</td><td className="px-6 py-3 text-muted-foreground">{Array.from(item.types).map((type) => KPI_TYPES.find((k) => k.v === type)?.label || type).join(", ")}</td>
            </tr>)}</tbody>
          </table>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-6 py-3"><SortButton label="Tanggal" sortKey="date" /></th>
                <th className="text-left px-6 py-3"><SortButton label="Member" sortKey="user" /></th>
                <th className="text-left px-6 py-3"><SortButton label="KPI" sortKey="kpi_type" /></th>
                <th className="text-right px-6 py-3"><SortButton label="Jumlah" sortKey="amount" /></th>
                <th className="text-left px-6 py-3">Catatan</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">Belum ada entry KPI yang sesuai filter.</td></tr>
              )}
              {pageItems.map((it) => (
                <tr key={it.id} className="border-t border-border hover:bg-accent/50" data-testid={`perf-row-${it.id}`}>
                  <td className="px-6 py-3 mono">{it.date}</td>
                  <td className="px-6 py-3">{userNames[it.user_id] || it.user_id}</td>
                  <td className="px-6 py-3">{KPI_TYPES.find((k) => k.v === it.kpi_type)?.label || it.kpi_type}</td>
                  <td className="px-6 py-3 text-right mono">{it.amount}</td>
                  <td className="px-6 py-3 text-muted-foreground">{it.note}</td>
                  <td className="px-6 py-3 text-right">
                    {!isReadOnly && (
                      <Button size="sm" variant="ghost" onClick={() => remove(it.id)} data-testid={`delete-perf-${it.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination ctrl={ctrl} testidPrefix="perf-pg" />
      </div>
    </div>
  );
}
